<template>
  <div class="preproduction-detail">
    <div class="page-header">
      <h2>预生产订单详情</h2>
      <a-space>
        <a-button
          v-if="!editMode && order && order.status !== 'CANCELLED'"
          @click="handleEdit"
        >
          编辑
        </a-button>
        <a-button
          v-if="editMode"
          type="primary"
          @click="handleSaveEdit"
          :loading="submitting"
        >
          保存
        </a-button>
        <a-button
          v-if="editMode"
          @click="handleCancelEdit"
        >
          取消
        </a-button>
        <a-button
          v-if="!editMode && order && order.status !== 'COMPLETED' && order.status !== 'CANCELLED'"
          type="primary"
          @click="handleComplete"
        >
          批次入库
        </a-button>
        <a-button @click="$router.back()">
          <template #icon>
            <ArrowLeftOutlined />
          </template>
          返回
        </a-button>
      </a-space>
    </div>

    <a-spin :spinning="loading">
      <a-card title="基本信息" class="info-card">
        <a-descriptions :column="3" bordered>
          <a-descriptions-item label="订单编号">
            {{ order?.preProductionNo || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="工厂">
            {{ order?.factoryName || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="订单日期">
            {{ order?.preProductionDate || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="getStatusColor(order?.status)">
              {{ getStatusText(order?.status) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="付款状态">
            <a-space>
              <a-tag :color="order?.paymentStatus === 'PAID' ? 'green' : 'orange'">
                {{ getPaymentStatusText(order?.paymentStatus) }}
              </a-tag>
              <a-button
                v-if="order"
                type="link"
                size="small"
                :loading="paymentSubmitting"
                @click="handleTogglePayment"
              >
                {{ order?.paymentStatus === 'PAID' ? '标记为未付款' : '标记为已付款' }}
              </a-button>
            </a-space>
          </a-descriptions-item>
          <a-descriptions-item label="总计划数量">
            {{ order?.totalQuantity || 0 }}
          </a-descriptions-item>
          <a-descriptions-item label="已完成数量">
            {{ order?.completedQuantity || 0 }}
          </a-descriptions-item>
          <a-descriptions-item label="待完成数量">
            {{ (order?.totalQuantity || 0) - (order?.completedQuantity || 0) }}
          </a-descriptions-item>
          <a-descriptions-item label="完成进度" :span="2">
            <a-progress
              :percent="calculateProgress()"
              :status="order?.status === 'COMPLETED' ? 'success' : 'active'"
            />
          </a-descriptions-item>
          <a-descriptions-item label="备注" :span="3">
            <a-textarea
              v-if="editMode"
              v-model:value="order.remark"
              :rows="2"
              placeholder="请输入备注"
              :maxlength="500"
              show-count
            />
            <template v-else>{{ order?.remark || '-' }}</template>
          </a-descriptions-item>
          <a-descriptions-item label="创建时间">
            {{ order?.createTime || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="更新时间" :span="2">
            {{ order?.updateTime || '-' }}
          </a-descriptions-item>
        </a-descriptions>
      </a-card>

      <a-card title="订单明细" class="info-card">
        <a-table
          :columns="itemColumns"
          :data-source="order?.items || []"
          :pagination="false"
          row-key="id"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'sku'">
              {{ record.color || '默认' }} / {{ record.size || '默认' }}
            </template>
            <template v-if="column.key === 'plannedQuantity'">
              <a-input-number
                v-if="editMode"
                v-model:value="record.plannedQuantity"
                :min="record.completedQuantity || 0"
                style="width: 100px"
              />
              <span v-else>{{ record.plannedQuantity }}</span>
            </template>
            <template v-if="column.key === 'remaining'">
              {{ record.plannedQuantity - record.completedQuantity }}
            </template>
            <template v-if="column.key === 'progress'">
              <a-progress
                :percent="calculateItemProgress(record)"
                size="small"
                :status="record.status === 'COMPLETED' ? 'success' : 'active'"
              />
            </template>
            <template v-if="column.key === 'status'">
              <a-tag :color="getStatusColor(record.status)">
                {{ getStatusText(record.status) }}
              </a-tag>
            </template>
            <template v-if="column.key === 'action'">
              <a-button
                v-if="Number(record.completedQuantity || 0) === 0 && order?.status !== 'CANCELLED'"
                type="link"
                size="small"
                danger
                @click="handleDeleteItemConfirm(record)"
              >
                删除
              </a-button>
            </template>
          </template>
        </a-table>
      </a-card>

      <a-card title="完成记录" class="info-card">
        <a-table
          :columns="completionColumns"
          :data-source="order?.completionRecords || []"
          :pagination="false"
          row-key="id"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'sku'">
              {{ record.color || '默认' }} / {{ record.size || '默认' }}
            </template>
          </template>
        </a-table>
      </a-card>
    </a-spin>

    <!-- 批次入库Modal -->
    <a-modal
      v-model:open="completeModalVisible"
      title="批次入库"
      @ok="handleCompleteSubmit"
      @cancel="completeModalVisible = false"
      :confirm-loading="submitting"
      width="900px"
    >
      <a-form layout="vertical">
        <a-form-item label="完成日期" required>
          <a-date-picker
            v-model:value="completeForm.completionDate"
            format="YYYY-MM-DD"
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="订单明细">
          <a-table
            :columns="completeColumns"
            :data-source="completeForm.items"
            :pagination="false"
            size="small"
            row-key="itemId"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'remainingQuantity'">
                {{ record.plannedQuantity - record.completedQuantity }}
              </template>
              <template v-if="column.key === 'completionQuantity'">
                <a-input-number
                  v-model:value="record.completionQuantity"
                  :min="0"
                  :max="record.plannedQuantity - record.completedQuantity"
                  style="width: 100px"
                />
              </template>
            </template>
          </a-table>
        </a-form-item>
        <a-form-item label="备注">
          <a-textarea
            v-model:value="completeForm.remarks"
            :rows="3"
            placeholder="请输入备注"
          />
        </a-form-item>
        <div style="text-align: right; padding: 10px; background: #f5f5f5; border-radius: 4px;">
          <span style="font-size: 14px;">
            总数量: <span style="font-weight: bold; color: #1890ff;">{{ totalCompletionQuantity }}</span>
          </span>
        </div>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { message, Modal } from 'ant-design-vue'
import { ArrowLeftOutlined } from '@ant-design/icons-vue'
import dayjs from 'dayjs'
import { preProductionApi } from '@/api'

export default {
  name: 'PreProductionDetail',
  components: {
    ArrowLeftOutlined
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const loading = ref(false)
    const submitting = ref(false)
    const paymentSubmitting = ref(false)
    const order = ref(null)
    const completeModalVisible = ref(false)
    const editMode = ref(false)
    const originalOrder = ref(null)

    const completeForm = reactive({
      completionDate: dayjs(),
      items: [],
      remarks: ''
    })

    const itemColumns = [
      {
        title: '商品',
        dataIndex: 'productName',
        key: 'productName',
        width: 150
      },
      {
        title: '仓库',
        dataIndex: 'warehouseName',
        key: 'warehouseName',
        width: 120
      },
      {
        title: 'SKU',
        key: 'sku',
        width: 120
      },
      {
        title: '计划数量',
        dataIndex: 'plannedQuantity',
        key: 'plannedQuantity',
        width: 100
      },
      {
        title: '已完成',
        dataIndex: 'completedQuantity',
        key: 'completedQuantity',
        width: 100
      },
      {
        title: '待完成',
        key: 'remaining',
        width: 100
      },
      {
        title: '完成进度',
        key: 'progress',
        width: 150
      },
      {
        title: '状态',
        key: 'status',
        width: 100
      },
      {
        title: '操作',
        key: 'action',
        width: 80
      }
    ]

    const completionColumns = [
      {
        title: '商品',
        dataIndex: 'productName',
        key: 'productName',
        width: 150
      },
      {
        title: '仓库',
        dataIndex: 'warehouseName',
        key: 'warehouseName',
        width: 120
      },
      {
        title: 'SKU',
        key: 'sku',
        width: 120
      },
      {
        title: '完成数量',
        dataIndex: 'completionQuantity',
        key: 'completionQuantity',
        width: 100
      },
      {
        title: '完成日期',
        dataIndex: 'completionDate',
        key: 'completionDate',
        width: 120
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 160
      }
    ]

    const completeColumns = [
      {
        title: '商品',
        dataIndex: 'productName',
        key: 'productName',
        width: 150
      },
      {
        title: '仓库',
        dataIndex: 'warehouseName',
        key: 'warehouseName',
        width: 100
      },
      {
        title: '颜色',
        dataIndex: 'color',
        key: 'color',
        width: 80
      },
      {
        title: '尺码',
        dataIndex: 'size',
        key: 'size',
        width: 80
      },
      {
        title: '计划数量',
        dataIndex: 'plannedQuantity',
        key: 'plannedQuantity',
        width: 80
      },
      {
        title: '已完成',
        dataIndex: 'completedQuantity',
        key: 'completedQuantity',
        width: 80
      },
      {
        title: '待完成',
        key: 'remainingQuantity',
        width: 80
      },
      {
        title: '本次完成',
        key: 'completionQuantity',
        width: 100
      }
    ]

    const totalCompletionQuantity = computed(() => {
      return completeForm.items.reduce((sum, item) => sum + (item.completionQuantity || 0), 0)
    })

    const totalCompletionAmount = computed(() => {
      return completeForm.items.reduce((sum, item) => {
        return sum + ((item.completionQuantity || 0) * (item.unitPrice || 0))
      }, 0).toFixed(2)
    })

    const calculateProgress = () => {
      if (!order.value || !order.value.totalQuantity || order.value.totalQuantity === 0) return 0
      return Math.round((order.value.completedQuantity || 0) / order.value.totalQuantity * 100)
    }

    const calculateItemProgress = (item) => {
      if (!item.plannedQuantity || item.plannedQuantity === 0) return 0
      return Math.round((item.completedQuantity || 0) / item.plannedQuantity * 100)
    }

    const getStatusColor = (status) => {
      const colors = {
        PENDING: 'orange',
        IN_PROGRESS: 'blue',
        COMPLETED: 'green',
        CANCELLED: 'red'
      }
      return colors[status] || 'default'
    }

    const getStatusText = (status) => {
      const texts = {
        PENDING: '待处理',
        IN_PROGRESS: '进行中',
        COMPLETED: '已完成',
        CANCELLED: '已取消'
      }
      return texts[status] || status
    }

    const getPaymentStatusText = (paymentStatus) => {
      return paymentStatus === 'PAID' ? '已付款' : '未付款'
    }

    const handleTogglePayment = async () => {
      if (!order.value) return
      const next = order.value.paymentStatus === 'PAID' ? 'UNPAID' : 'PAID'
      try {
        paymentSubmitting.value = true
        const result = await preProductionApi.updatePaymentStatus({
          preProductionId: order.value.id,
          paymentStatus: next
        })
        if (result.code === '0') {
          order.value.paymentStatus = next
          message.success(next === 'PAID' ? '已标记为已付款' : '已标记为未付款')
        } else {
          message.error(result.msg || '更新付款状态失败')
        }
      } catch (error) {
        console.error('更新付款状态失败:', error)
        message.error('更新付款状态失败')
      } finally {
        paymentSubmitting.value = false
      }
    }

    const loadDetail = async () => {
      const id = route.params.id
      if (!id) {
        message.error('订单ID不存在')
        router.back()
        return
      }

      loading.value = true
      try {
        const result = await preProductionApi.detail(id)
        if (result.code === '0') {
          order.value = result.data
        } else {
          message.error(result.msg || '加载订单详情失败')
        }
      } catch (error) {
        console.error('加载订单详情失败:', error)
        message.error('加载订单详情失败')
      } finally {
        loading.value = false
      }
    }

    const handleComplete = () => {
      completeForm.completionDate = dayjs()
      completeForm.remarks = ''
      completeForm.items = order.value.items.map(item => ({
        itemId: item.id,
        productName: item.productName,
        warehouseName: item.warehouseName,
        color: item.color,
        size: item.size,
        plannedQuantity: item.plannedQuantity,
        completedQuantity: item.completedQuantity,
        completionQuantity: undefined,
        unitPrice: 0
      }))
      completeModalVisible.value = true
    }

    const calculateTotals = () => {
      // Trigger computed properties update
    }

    const handleCompleteSubmit = async () => {
      if (!completeForm.completionDate) {
        message.error('请选择完成日期')
        return
      }

      const validItems = completeForm.items.filter(item => item.completionQuantity > 0)
      if (validItems.length === 0) {
        message.error('请至少完成一个SKU')
        return
      }

      // 验证完成数量不能超过待完成数量
      const invalidItems = validItems.filter(item => {
        const remaining = item.plannedQuantity - item.completedQuantity
        return item.completionQuantity > remaining
      })

      if (invalidItems.length > 0) {
        const firstInvalid = invalidItems[0]
        const remaining = firstInvalid.plannedQuantity - firstInvalid.completedQuantity
        message.error(`${firstInvalid.productName}（${firstInvalid.color || '默认'}/${firstInvalid.size || '默认'}）的完成数量不能超过待完成数量 ${remaining}`)
        return
      }

      try {
        submitting.value = true
        const result = await preProductionApi.complete({
          preProductionId: order.value.id,
          completionDate: completeForm.completionDate.format('YYYYMMDD'),
          remark: completeForm.remarks,
          items: validItems.map(item => ({
            preProductionItemId: item.itemId,
            completionQuantity: item.completionQuantity,
            unitPrice: item.unitPrice || 0
          }))
        })

        if (result.code === '0') {
          message.success('批次入库成功')
          completeModalVisible.value = false
          loadDetail()
        } else {
          message.error(result.msg || '批次入库失败')
        }
      } catch (error) {
        console.error('批次入库失败:', error)
        message.error('批次入库失败')
      } finally {
        submitting.value = false
      }
    }

    const handleDeleteItem = async (record) => {
      try {
        const result = await preProductionApi.deleteItem(record.id)
        if (result.code === '0') {
          message.success('删除明细成功')
          loadDetail()
        } else {
          message.error(result.msg || '删除明细失败')
        }
      } catch (error) {
        console.error('删除明细失败:', error)
        message.error('删除明细失败')
      }
    }

    const handleDeleteItemConfirm = (record) => {
      Modal.confirm({
        title: '确认删除',
        content: `确定删除明细「${record.productName}」吗？`,
        okText: '确定',
        cancelText: '取消',
        okType: 'danger',
        onOk: () => handleDeleteItem(record)
      })
    }

    const handleEdit = () => {
      // Save original order data for cancel
      originalOrder.value = JSON.parse(JSON.stringify(order.value))
      editMode.value = true
    }

    const handleCancelEdit = () => {
      // Restore original order data
      order.value = JSON.parse(JSON.stringify(originalOrder.value))
      editMode.value = false
      originalOrder.value = null
    }

    const handleSaveEdit = async () => {
      // Validate
      const invalidItems = order.value.items.filter(item =>
        Number(item.plannedQuantity) < Number(item.completedQuantity || 0)
      )

      if (invalidItems.length > 0) {
        const firstInvalid = invalidItems[0]
        message.error(`${firstInvalid.productName}的计划数量(${firstInvalid.plannedQuantity})不能小于已完成数量(${firstInvalid.completedQuantity || 0})`)
        return
      }

      try {
        submitting.value = true
        const result = await preProductionApi.update({
          preProductionId: order.value.id,
          remark: order.value.remark || '',
          items: order.value.items.map(item => ({
            itemId: item.id,
            plannedQuantity: item.plannedQuantity
          }))
        })

        if (result.code === '0') {
          message.success('更新预生产订单成功')
          editMode.value = false
          originalOrder.value = null
          loadDetail()
        } else {
          message.error(result.msg || '更新预生产订单失败')
        }
      } catch (error) {
        console.error('更新预生产订单失败:', error)
        message.error('更新预生产订单失败')
      } finally {
        submitting.value = false
      }
    }

    onMounted(() => {
      loadDetail()
    })

    return {
      loading,
      submitting,
      paymentSubmitting,
      order,
      completeModalVisible,
      editMode,
      completeForm,
      itemColumns,
      completionColumns,
      completeColumns,
      totalCompletionQuantity,
      totalCompletionAmount,
      calculateProgress,
      calculateItemProgress,
      getStatusColor,
      getStatusText,
      getPaymentStatusText,
      handleTogglePayment,
      handleComplete,
      handleDeleteItem,
      handleDeleteItemConfirm,
      handleEdit,
      handleCancelEdit,
      handleSaveEdit,
      calculateTotals,
      handleCompleteSubmit
    }
  }
}
</script>

<style scoped>
.preproduction-detail {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.info-card {
  margin-bottom: 24px;
}
</style>
